import { inject, Injectable, NgZone } from '@angular/core';
import { SHARED_ENV } from '@trading-fe/shared';

import { Grecaptcha } from '../../models';

declare const grecaptcha: Grecaptcha;

export class RecaptchaNotLoadedError extends Error {
  constructor() {
    super('reCAPTCHA Enterprise not loaded');
    this.name = 'RecaptchaNotLoadedError';
  }
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private env = inject(SHARED_ENV);
  private ngZone = inject(NgZone);
  private scriptLoaded = false;

  /**
   * Always resolves with a token string (never rejects).
   * Returns empty string if reCAPTCHA is unavailable.
   */
  async execute(action: string): Promise<string> {
    try {
      this.loadScript();
      await this.waitForRecaptcha();
    } catch {
      return '';
    }

    return new Promise<string>((resolve) => {
      const timeout = setTimeout(() => {
        this.ngZone.run(() => resolve(''));
      }, 10000);

      grecaptcha.enterprise.ready(async () => {
        clearTimeout(timeout);
        try {
          const token: string = await grecaptcha.enterprise.execute(
            this.env.reCaptchaSiteKey,
            { action }
          );
          this.ngZone.run(() => resolve(token || ''));
        } catch {
          this.ngZone.run(() => resolve(''));
        }
      });
    });
  }

  private loadScript(): void {
    if (this.scriptLoaded) return;
    this.scriptLoaded = true;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.env.reCaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  private waitForRecaptcha(timeout = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        resolve();
        return;
      }

      const interval = 200;
      let elapsed = 0;

      const timer = setInterval(() => {
        elapsed += interval;
        if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
          clearInterval(timer);
          resolve();
        } else if (elapsed >= timeout) {
          clearInterval(timer);
          reject(new RecaptchaNotLoadedError());
        }
      }, interval);
    });
  }
}
